import { Cookies } from "react-cookie";
import { getSiteDomain } from "@/utils/site";
import { AUTHORIZATION } from "@/internals/constants/cookies";
import { PATHS } from "@/internals/constants/paths";
import { TokenData } from "./types";
import { isJwtTokenExpired } from "@/utils/tokenUtils";

export const setCookie = (name: string, value: any, expiration?: Date) => {
  const cookies = new Cookies();
  cookies.set(name, value, {
    domain: getCookieDomain(),
    expires: expiration ? new Date(expiration) : undefined,
    path: PATHS.HOME,
  });
};

export const getCookie = (name: string): unknown | undefined => {
  try {
    const cookies = new Cookies();
    return cookies.get(name);
  } catch (error) {
    console.error("Error getting cookie:", error);
    return undefined;
  }
};

export const getAuthCookie = () => {
  const cookies = new Cookies();
  return cookies.get(AUTHORIZATION);
};

export const removeCookie = (name: string) => {
  const cookies = new Cookies();
  cookies.remove(name, { path: PATHS.HOME, domain: getCookieDomain() });
};

const getAuthCookieSetOptions = (): {
  path: string;
  domain: string | undefined;
  secure: boolean;
  sameSite: "lax";
} => {
  const isSecureContext =
    typeof window !== "undefined" && typeof window.isSecureContext === "boolean"
      ? window.isSecureContext
      : process.env.NEXT_PUBLIC_ENV === "prod";

  return {
    path: PATHS.HOME,
    domain: process.env.NEXT_PUBLIC_ENV === "prod" ? getCookieDomain() : undefined,
    // Secure cookies are ignored on plain HTTP (e.g. local custom host). HTTPS → isSecureContext true.
    secure: isSecureContext,
    sameSite: "lax",
  };
};

export const setAuthCookie = (token: string, expiration?: string) => {
  const cookies = new Cookies();
  const base = getAuthCookieSetOptions();

  cookies.set(AUTHORIZATION, token, {
    ...base,
    expires: expiration ? new Date(expiration) : undefined,
  });
};

export const removeAuthCookie = () => {
  const cookies = new Cookies();
  const base = getAuthCookieSetOptions();
  cookies.remove(AUTHORIZATION, { ...base, domain: undefined });
  cookies.remove(AUTHORIZATION, { ...base, domain: getCookieDomain() });
};

export const getTokenData = (): TokenData => {
  const cookies = new Cookies();
  const token = cookies.get(AUTHORIZATION);

  if (!token) return undefined;

  return {
    token,
    isTokenExpired: isJwtTokenExpired(token),
  };
};

export const isUserLoggedIn = () => {
  const cookies = new Cookies();
  return cookies.get(AUTHORIZATION);
};

export const getCookieDomain = () => {
  return `.${getSiteDomain()}`;
};
